import { useState, Fragment, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { Button, styled, Grid, Alert, CircularProgress } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { v4 as uuidv4 } from "uuid";
import clsx from "clsx";

const Container = styled("div")({
  border: "3px dashed #757575",
  borderRadius: 10,
  padding: 35,
  textAlign: "center",
  marginTop: 8,
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const ImagePreviewContainer = styled("div")({
  display: "flex",
  flexWrap: "wrap",
  gap: "10px",
  marginTop: 10,
  justifyContent: "center",
  width: "100%",
});

const ImageWrapper = styled("div")({
  position: "relative",
  width: "150px",
  height: "150px",
  borderRadius: "10px",
  overflow: "hidden",
  border: "2px solid #ccc",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#f5f5f5",
});

const ImagePreview = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "contain",
  borderRadius: "10px",
});

const CROP_SIZES = {
  HERO: { minWidth: 1200, minHeight: 640, label: "Hero Image" },
  GALLERY: { minWidth: 1200, label: "Gallery Image" },
  CARD: { minWidth: 700, minHeight: 260, label: "Card Image" },
  MAIN: { minWidth: 700, minHeight: 260, label: "Card Image" },
  LOGO: { minWidth: 120, label: "Logo Image" },
  PROFILE: { minWidth: 180, minHeight: 180, label: "Profile Image" },
  EXTRA_CARD: { minWidth: 600, label: "Extra Card Image" }
};

const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/jpg", "image/webp"];

const ComponentImageUploadWithCrop = ({
  imageType,
  onChange,
  createSagaUploadImage,
  componentError,
  componentMeesageError,
  initialImages = [],
  disabled = false,
  maxImages,
  uploadResponses,
  uploadErrors,
}) => {
  const [images, setImages] = useState([]);
  const [errorText, setErrorText] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const processedUploads = useRef(new Set());
  const uploadKeyMap = useRef({});
  const inputRef = useRef();
  const parsedMaxImages = Number(maxImages);
  const allowedMaxImages = (() => {
    if (imageType !== "GALLERY") {
      return 1;
    }
    if (Number.isFinite(parsedMaxImages) && parsedMaxImages > 0) {
      return parsedMaxImages;
    }
    return 5;
  })();


  useEffect(() => {
    if (Array.isArray(initialImages)) {
      if (initialImages.length > 0) {
        const mappedInitialImages = initialImages.map((img) => ({
          id: img.uri,
          cloudinary: { secure_url: img.uri, public_id: img.uri },
          status: "preloaded",
          alt: img.alt,
          type: img.type,
        }));
        setImages(mappedInitialImages.slice(0, allowedMaxImages));
      } else {
        // When parent clears, also clear local state and input
        setImages([]);
        setErrorText("");
        if (inputRef?.current) inputRef.current.value = null;
      }
    }
  }, [initialImages, allowedMaxImages]);

  const handleImageUpload = async (event) => {
    setErrorText("");
    const files = Array.from(event.target.files);
    const validFiles = files.filter((file) => file.type.startsWith("image/"));

    if (validFiles.length !== files.length) {
      setErrorText("Some files were not images.");
      return;
    }

    const remainingSlots = allowedMaxImages - images.length;
    const filesToProcess = validFiles.slice(0, remainingSlots);

    if (filesToProcess.length === 0) {
      setErrorText(`You can only upload up to ${allowedMaxImages} images.`);
      return;
    }

    setIsUploading(true);
    let hasError = false;

    for (const file of filesToProcess) {
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        setErrorText(`Only JPEG, PNG and WEBP files are allowed.`);
        hasError = true;
        continue;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        setErrorText(`File size should not exceed 2MB.`);
        hasError = true;
        continue;
      }

      const img = new Image();
      img.src = URL.createObjectURL(file);

      await new Promise((resolve) => {
        img.onload = () => {
          const { minWidth, maxWidth, minHeight, maxHeight, label } = CROP_SIZES[imageType] || {};

          if (minHeight === undefined && maxHeight === undefined) {
            if (img.width < minWidth || (maxWidth && img.width > maxWidth)) {
              const widthMessage = maxWidth
                ? `between ${minWidth}px and ${maxWidth}px`
                : `at least ${minWidth}px`;

              setErrorText(
                `${label} must be ${widthMessage} wide. Uploaded image width is ${img.width}px.`
              );
              hasError = true;
            } else {
              const headersSetCookie = window.localStorage.getItem("headersSetCookie");
              const individualKey = uuidv4();
              uploadKeyMap.current[individualKey] = true;
              createSagaUploadImage(headersSetCookie, {
                file,
                type: imageType,
                uploadKey: individualKey,
              });
            }
          } else {

            const widthValid = img.width >= minWidth && img.width <= (maxWidth || Infinity);
            const heightValid = img.height >= minHeight && img.height <= (maxHeight || Infinity);

            if (!widthValid || !heightValid) {
              setErrorText(
                `${label} must be between ${minWidth}px and ${maxWidth || "∞"}px wide, ` +
                `and between ${minHeight}px and ${maxHeight || "∞"}px high. ` +
                `Uploaded image size is ${img.width}x${img.height}px.`
              );
              hasError = true;
            } else {
              const headersSetCookie = window.localStorage.getItem("headersSetCookie");
              const individualKey = uuidv4();
              uploadKeyMap.current[individualKey] = true;
              createSagaUploadImage(headersSetCookie, {
                file,
                type: imageType,
                uploadKey: individualKey,
              });
            }
          }
          resolve();
        };

        img.onerror = () => {
          setErrorText("Error loading the image file.");
          hasError = true;
          resolve();
        };
      });
    }

    if (hasError) setIsUploading(false);
  };

  const handleRemoveImage = (id) => {
    const updatedImages = images.filter((img) => img.id !== id);
    setImages(updatedImages);
    setErrorText("");
    if (onChange) onChange(updatedImages);
    if (inputRef?.current) inputRef.current.value = null;
  };

  useEffect(() => {
    for (const [key] of Object.entries(uploadKeyMap.current)) {
      if (processedUploads.current.has(key)) continue;

      const response = uploadResponses[key];
      const error = uploadErrors[key];

      if (error) {
        setErrorText(error);
        setIsUploading(false);
        processedUploads.current.add(key);
      } else if (response) {
        const newImage = {
          id: response.asset_id,
          cloudinary: response,
          status: "uploaded",
          type: imageType,
        };
        setImages((prevImages) => {

          let updatedImagesArray = [...prevImages, newImage].slice(-allowedMaxImages);

          if (onChange) onChange(updatedImagesArray);
          return updatedImagesArray;
        });

        setErrorText("");
        processedUploads.current.add(key);
      }
    }



    const allProcessed = Object.keys(uploadKeyMap.current).every((key) =>
      processedUploads.current.has(key)
    );
    if (allProcessed) setIsUploading(false);
  }, [uploadResponses, uploadErrors]);

  return (
    <Fragment>
      <Container>
        {errorText && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorText}
          </Alert>
        )}
        <Grid container spacing={2} alignItems="center" justifyContent="center">
          <Grid item>
            {!disabled && (
              <Button
                variant="contained"
                component="label"
                startIcon={!isUploading && <CloudUploadIcon />}
                disabled={isUploading || images.length >= allowedMaxImages}
              >
                {isUploading ? <CircularProgress size={24} color="inherit" /> : "Upload Image"}
                <input
                  ref={inputRef}
                  type="file"
                  multiple={allowedMaxImages > 1}
                  accept={ALLOWED_IMAGE_TYPES.join(",")}
                  onChange={handleImageUpload}
                  style={{ display: "none" }}
                />
              </Button>
            )}
          </Grid>
        </Grid>

        {images.length > 0 && (
          <ImagePreviewContainer>
            {images.map((img) => (
              <ImageWrapper key={img.id}>
                <ImagePreview src={img.cloudinary.secure_url} alt="Uploaded" />
                {!disabled && (
                  <Button
                    onClick={() => handleRemoveImage(img.id)}
                    style={{
                      position: "absolute",
                      top: 5,
                      right: 5,
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      color: "white",
                      padding: "1px",
                      fontSize: "12px",
                      cursor: "pointer",
                    }}
                  >
                    X
                  </Button>
                )}
              </ImageWrapper>
            ))}
          </ImagePreviewContainer>
        )}
      </Container>
      {componentError && (
        <div className={`inputHelperTextContainer spaceBetween`}>
          <div className={clsx("helperText", { error: componentError })}>{componentMeesageError}</div>
        </div>
      )}
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  uploadResponses: state.uploadImageReducer.uploadResponses,
  uploadErrors: state.uploadImageReducer.uploadErrors,
});

const mapDispatchToProps = (dispatch) => ({
  createSagaUploadImage: (access, payload) =>
    dispatch({ type: "CREATE_UPLOAD_IMAGE", access, payload }),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ComponentImageUploadWithCrop);
